import { useLingui } from '@lingui/react/macro';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { Dialog as SheetPrimitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { Button } from './button';
import { cn } from './cn';

export const Sheet = SheetPrimitive.Root;
export const SheetTrigger = SheetPrimitive.Trigger;
export const SheetClose = SheetPrimitive.Close;

const sheetVariants = cva(
  'fixed inset-y-0 z-50 flex w-full flex-col bg-popover text-popover-foreground shadow-3 outline-none',
  {
    variants: {
      side: {
        right: 'right-0 border-l border-border data-[state=closed]:animate-slide-out-right data-[state=open]:animate-slide-in-right',
        left: 'left-0 border-r border-border data-[state=closed]:animate-slide-out-left data-[state=open]:animate-slide-in-left',
      },
      // md ≈ 612px e sm ≈ 500px, as larguras do DrawerComponent do legado (sm = sheet empilhado).
      size: { md: 'max-w-[38rem]', sm: 'max-w-[31rem]' },
    },
    defaultVariants: { side: 'right', size: 'md' },
  },
);

export type SheetContentProps = ComponentProps<typeof SheetPrimitive.Content> &
  VariantProps<typeof sheetVariants> & { readonly showClose?: boolean };

/** Painel lateral (web). No celular, o padrão (E5) troca por `Drawer` ou tela cheia (U12). */
export function SheetContent({ className, side, size, showClose = true, children, ...props }: SheetContentProps) {
  const { t } = useLingui();
  return (
    <SheetPrimitive.Portal>
      <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-scrim data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
      <SheetPrimitive.Content data-slot="sheet-content" className={cn(sheetVariants({ side, size }), className)} {...props}>
        {children}
        {showClose ? (
          <SheetPrimitive.Close asChild>
            <Button variant="ghost" size="icon" aria-label={t`Fechar`} className="absolute top-3.5 right-3.5 size-8 text-muted-foreground">
              <X aria-hidden />
            </Button>
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Content>
    </SheetPrimitive.Portal>
  );
}

export function SheetHeader({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('grid gap-1 border-b border-border px-6 py-4 pr-14', className)} {...props} />;
}

export function SheetBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-5', className)} {...props} />;
}

export function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 border-t border-border px-6 py-3 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export function SheetTitle({ className, ...props }: ComponentProps<typeof SheetPrimitive.Title>) {
  return <SheetPrimitive.Title className={cn('text-lg font-semibold', className)} {...props} />;
}

export function SheetDescription({ className, ...props }: ComponentProps<typeof SheetPrimitive.Description>) {
  return <SheetPrimitive.Description className={cn('text-sm text-muted-foreground', className)} {...props} />;
}
